import { useEffect, useRef, useState, type CSSProperties } from "react";

interface BarcodeDetectorResult {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<BarcodeDetectorResult[]>;
}

declare global {
  interface Window {
    BarcodeDetector?: new (options?: {
      formats: string[];
    }) => BarcodeDetectorLike;
  }
}

/** True only when the browser exposes a native QR-capable camera detector. */
export function isQrCameraScannerSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.BarcodeDetector === "function" &&
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function"
  );
}

/**
 * A browser-native camera QR reader shared by authenticated operational flows.
 * It forwards a decoded opaque value directly to the caller and never renders
 * that value into the DOM or an accessible name.
 */
export function CameraQrScannerPane({
  active,
  ariaLabel,
  onDetected,
  style,
}: {
  active: boolean;
  ariaLabel: string;
  onDetected: (value: string) => void;
  style?: CSSProperties;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onDetectedRef = useRef(onDetected);
  const [cameraError, setCameraError] = useState<string | null>(null);

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let decoded = false;
    let stream: MediaStream | null = null;
    let animationFrame: number | null = null;
    const stopStream = () => stream?.getTracks().forEach((track) => track.stop());
    setCameraError(null);

    async function start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (cancelled) {
          stopStream();
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const Detector = window.BarcodeDetector;
        if (!Detector) {
          stopStream();
          if (!cancelled)
            setCameraError(
              "Camera scanning is unavailable. Enter the printed reference.",
            );
          return;
        }
        const detector = new Detector({ formats: ["qr_code"] });
        const scan = async () => {
          if (cancelled || decoded || !videoRef.current) return;
          try {
            const results = await detector.detect(videoRef.current);
            const value = results[0]?.rawValue;
            if (value) {
              decoded = true;
              if (animationFrame !== null) cancelAnimationFrame(animationFrame);
              stopStream();
              onDetectedRef.current(value);
              return;
            }
          } catch {
            // Empty and partially decoded frames are expected while scanning.
          }
          if (!cancelled) {
            animationFrame = requestAnimationFrame(() => void scan());
          }
        };
        animationFrame = requestAnimationFrame(() => void scan());
      } catch {
        stopStream();
        if (!cancelled) {
          setCameraError(
            "Camera access is unavailable. Enter the printed reference.",
          );
        }
      }
    }

    void start();
    return () => {
      cancelled = true;
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      stopStream();
    };
  }, [active]);

  if (cameraError) return <div role="alert">{cameraError}</div>;

  return (
    <video
      ref={videoRef}
      muted
      playsInline
      aria-label={ariaLabel}
      style={{
        width: "100%",
        aspectRatio: "16 / 9",
        borderRadius: "4px",
        backgroundColor: "#000",
        objectFit: "cover",
        ...style,
      }}
    />
  );
}
